import React, { useState, useCallback, useRef, useMemo } from 'react';
import { View, Text, Animated, FlatList, ViewStyle, StyleProp, NativeScrollEvent, NativeSyntheticEvent } from 'react-native';
import { AnimatedTableScrollContext } from './context/AnimatedTableScrollContext';
import TouchableElement from '@components/common/touchableElement';
import i18n from 'i18n-js';
import { getBoxShadowStyle } from '@styles/shadow';
import { font as initialFont } from '@styles/fonts';
import { scale as initialScale } from '@components/frame';
import { SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import AnimatedTableHeader from './components/AnimatedTableHeader';
import NoData from './components/NoData';
import ShowMoreButton from './components/ShowMoreButton';
import AnimatedRow, { RenderAccordionContentParams } from './components/AnimatedRow';
import TableContent from './components/TableContent';
import { useAccordionAutoScroll } from './hooks/useAccordionAutoScroll';
import { ColumnConfig, CellRenderer } from './types';
import { ColorFunction } from '@styles/colors/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';

export type { RenderAccordionContentParams };

export interface AnimatedTableProps<T = any> {
  array: T[];
  C: ColorFunction;
  config: ColumnConfig<T>;
  cellRenderer?: CellRenderer<T>;
  headerSize?: number;
  lineSize?: number;
  showMoreSize?: number;
  baseCount?: number;
  showMoreText?: string | null;
  showMoreArrow?: boolean;
  onPress?: (item: T) => void;
  onPressIn?: (item: T) => void;
  onSubRowPress?: (item: T) => void;
  onSubRowPressIn?: (item: T) => void;
  showAllButton?: boolean;
  loaded?: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  load?: (params: any) => void;
  initialFilter?: string;
  initialDirection?: boolean;
  borderRadiusConfig?: any;
  valuesAlreadyTranslated?: boolean;
  stickyHeader?: boolean;
  autoScrollOnExpand?: boolean;
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
  selectedItem?: T | null;
  getIsSelectedRow?: (item: T, selectedItem: T | null | undefined) => boolean;
  getHasOverlay?: (item: T, selectedItem: T | null | undefined) => boolean;
  enableAccordion?: boolean;
  getRowKey?: (item: T, index: number) => string | number;
  renderAccordionContent?: (params: RenderAccordionContentParams<T>) => React.ReactNode;
  getAccordionItemsCount?: (accordionData: any) => number;
  expandedRows?: Record<string | number, any>;
  keyExtractorField?: keyof T;
  styleForLastItem?: StyleProp<ViewStyle>;
  hideBorderForLastItem?: boolean;
  getRowTestID?: (item: T, index: number) => string | undefined;
}

export default function AnimatedTable<T = any>(props: AnimatedTableProps<T>) {
  let {
    array,
    C,
    config,
    cellRenderer,
    headerSize = 50,
    lineSize = 54,
    showMoreSize = 54,
    baseCount = 2,
    showMoreText = null,
    showMoreArrow = true,
    onPress = () => {},
    onSubRowPress,
    showAllButton = false,
    loaded,
    scale = initialScale,
    font = initialFont,
    load,
    initialFilter = '',
    initialDirection = false,
    borderRadiusConfig,
    valuesAlreadyTranslated = false,
    stickyHeader = false,
    autoScrollOnExpand = false,
    onEndReached,
    onEndReachedThreshold = 0.5,
    selectedItem = null,
    getIsSelectedRow,
    getHasOverlay,
    enableAccordion = false,
    getRowKey,
    renderAccordionContent,
    getAccordionItemsCount,
    expandedRows,
    keyExtractorField,
    styleForLastItem,
    hideBorderForLastItem,
    getRowTestID,
  } = props;

  showMoreText = showMoreText ? showMoreText : `${i18n.t('SHOW ALL')} ${array.length}`;

  const [filter, setFilter] = useState(initialFilter);
  const [direction, setDirection] = useState(initialDirection);
  const [loading, setLoading] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);

  const getItemKey = (item: T, index: number): string => {
    if (keyExtractorField && item[keyExtractorField] != null) {
      return String(item[keyExtractorField]);
    }
    if ('name' in item && item.name != null) {
      return String(item.name);
    }
    return String(index);
  };

  const filterPress = (name) => {
    let dir = !direction;
    if (name === filter) {
      setDirection(dir);
    } else {
      setFilter(name);
      setDirection(false);
      dir = false;
    }
    if (load) {
      load({
        reset: true,
        orderBy: {
          [name]: dir ? 'asc' : 'desc',
        },
      });
    }
  };

  const renderArrow = (name, c) => {
    if (name === filter) {
      return (
        <Text
          style={[
            {
              color: c,
              fontSize: scale(13),
              fontWeight: 'bold',
              marginLeft: scale(2),
            },
            font['Button 2'],
          ]}
        >
          {direction ? '↑' : '↓'}
        </Text>
      );
    }
  };

  const content = (
    <>
      <AnimatedTableHeader {...{ config: config, renderArrow, headerSize, filterPress, C, font, scale }} />
      {!array.length && <NoData {...{ C, loaded, font, scale, lineSize }} />}
      <TableContent
        {...{
          array,
          C,
          config: config,
          lineSize,
          onPress,
          onSubRowPress,
          showAllButton,
          valuesAlreadyTranslated,
          font,
          scale,
          containerWidth,
          selectedItem,
          cellRenderer,
          renderAccordionContent,
          getAccordionItemsCount,
          styleForLastItem,
          hideBorderForLastItem,
        }}
      />
      {showAllButton && loaded && <ShowMoreButton {...{ C, load, loading, setLoading, showMoreArrow, showMoreSize, showMoreText, font, scale }} />}
    </>
  );

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });
  const containerStyles = {
    backgroundColor: C('Background / Light / Field'),
    width: '100%',
  };
  const borderRadiusStyles = borderRadiusConfig ? borderRadiusConfig : { borderRadius: scale(SCREEN_BORDER_RADIUS) };

  const {
    flatListRef,
    onScroll: autoScrollOnScroll,
    onLayout: autoScrollOnLayout,
  } = useAccordionAutoScroll({
    enabled: autoScrollOnExpand,
    array,
    expandedRows,
    scaledLineSize: scale(lineSize),
    getRowKey,
    getAccordionItemsCount,
  });

  const scrollOffsetYRef = useRef(0);
  const scrollContextValue = useMemo(() => ({ scrollOffsetYRef }), []);

  const handleScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollOffsetYRef.current = e.nativeEvent.contentOffset.y;
      autoScrollOnScroll(e);
    },
    [autoScrollOnScroll]
  );

  if (stickyHeader) {
    const renderItem = ({ item, index }) => {
      const isSelectedRow = getIsSelectedRow ? getIsSelectedRow(item, selectedItem) : false;
      const hasOverlay = getHasOverlay ? getHasOverlay(item, selectedItem) : false;

      return (
        <TouchableElement
          onPress={() => onPress(item)}
          key={`arrayForRender-${getItemKey(item, index)}-${index}`}
          hitSlop={{ top: 0, bottom: 0, left: 0, right: 0 }}
          testID={getRowTestID?.(item, index)}
        >
          {({ boxStyle, pressHoverColor }) => {
            const rowBackgroundColor = isSelectedRow ? C('Background/Light/Event Trace') : C(pressHoverColor('Background/Light/Field'));

            return (
              <View
                style={{
                  backgroundColor: rowBackgroundColor,
                  width: '100%',
                  position: 'relative',
                }}
              >
                <AnimatedRow
                  item={item}
                  index={index}
                  boxStyle={boxStyle}
                  pressHoverColor={pressHoverColor}
                  config={config}
                  C={C}
                  valuesAlreadyTranslated={valuesAlreadyTranslated}
                  font={font}
                  scale={scale}
                  lineSize={lineSize}
                  showAllButton={showAllButton}
                  array={array}
                  containerWidth={containerWidth}
                  selectedItem={selectedItem}
                  getIsSelectedRow={getIsSelectedRow}
                  enableAccordion={enableAccordion}
                  getRowKey={getRowKey}
                  cellRenderer={cellRenderer}
                  onSubRowPress={onSubRowPress}
                  renderAccordionContent={renderAccordionContent}
                  getAccordionItemsCount={getAccordionItemsCount}
                />
                {hasOverlay && (
                  <View
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: C('Overlay/Light/Soften Strong'),
                      zIndex: 10000000,
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </View>
            );
          }}
        </TouchableElement>
      );
    };

    // Memoize header and footer components
    const ListHeaderComponent = useCallback(
      () => <>{!array.length && <NoData {...{ C, loaded, font, scale, lineSize }} />}</>,
      [array.length, C, loaded, font, scale, lineSize]
    );

    const ListFooterComponent = useCallback(
      () => (
        <>
          {showAllButton && loaded && (
            <ShowMoreButton {...{ C, load, loading, setLoading, showMoreArrow, showMoreSize, showMoreText, font, scale }} />
          )}
        </>
      ),
      [showAllButton, loaded, C, load, loading, setLoading, showMoreArrow, showMoreSize, showMoreText, font, scale]
    );

    return (
      <AnimatedTableScrollContext.Provider value={scrollContextValue}>
        <Animated.View
          style={[shadowStyles, containerStyles, borderRadiusStyles, { flex: 1 }]}
          onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
        >
          <AnimatedTableHeader {...{ config: config, renderArrow, headerSize, filterPress, C, font, scale }} />
          <FlatList
            ref={flatListRef}
            onScroll={handleScroll}
            onLayout={autoScrollOnLayout}
            scrollEventThrottle={16}
            onScrollToIndexFailed={(info) => {
              const fallbackOffset = info.averageItemLength * info.index;
              flatListRef.current?.scrollToOffset({ offset: fallbackOffset, animated: true });
            }}
            style={{ flex: 1 }}
            data={array}
            renderItem={renderItem}
            keyExtractor={(item, index) => `flatList-${getItemKey(item, index)}-${index}`}
            ListHeaderComponent={ListHeaderComponent}
            ListFooterComponent={ListFooterComponent}
            showsVerticalScrollIndicator={false}
            onEndReached={onEndReached}
            onEndReachedThreshold={onEndReachedThreshold}
            extraData={expandedRows}
            removeClippedSubviews={false}
          />
        </Animated.View>
      </AnimatedTableScrollContext.Provider>
    );
  }

  return (
    <Animated.View style={[shadowStyles, containerStyles, borderRadiusStyles]} onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}>
      {content}
    </Animated.View>
  );
}
